import moment from 'moment';
import { AccountData, EnrichedEventData, OrderData, OrderPageData, WorkDayType, WorkingDaysData } from '../../api';
import { CalendarItems } from '../calendarWrapper/CalendarWrapper';
import { getOrderPages } from '../../utils/AccountUtils';
import { seatedTableCount } from './orderSeating';

export interface DayCapacityStats {
  date: string;
  totalWorkingMinutes: number;
  usedMinutes: number;
  usagePercentage: number;
  orderCount: number;
  eventCount: number;
  personCount: number;
}

export interface PeriodCapacityStats {
  period: string;
  totalWorkingMinutes: number;
  usedMinutes: number;
  usagePercentage: number;
  orderCount: number;
  eventCount: number;
  avgDailyUsage: number;
  personCount: number;
}

export interface CapacityStats {
  daily: DayCapacityStats[];
  weekly: PeriodCapacityStats[];
  monthly: PeriodCapacityStats[];
}

// Get working hours for a specific day of week
const getDayWorkingMinutes = (workingDays: WorkingDaysData, dayOfWeek: number): number => {
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayName = dayNames[dayOfWeek] as keyof WorkingDaysData;
  const dayData = workingDays[dayName];

  if (!dayData || dayData.work_day_type !== WorkDayType.INTERVAL || !dayData.from_time || !dayData.to_time) {
    return 0;
  }

  return (dayData.to_time - dayData.from_time) / 60; // Convert seconds to minutes
};

// Calculate total capacity considering shared resources and regular workshops for a given date
const getTotalCapacityMinutesForDate = (date: string, orderPages: OrderPageData[], account: AccountData): number => {
  const momentDate = moment(date);
  const dayOfWeek = momentDate.day(); // 0 = Sunday, 1 = Monday, etc.

  let totalCapacityMinutes = 0;

  // Calculate capacity from shared resources (if any)
  const schedulerConfigs = account?.shared_resources?.scheduler_configs || [];

  for (const config of schedulerConfigs) {
    // Find an order page that uses this scheduler config to get working hours
    const orderPageWithConfig = orderPages.find((page) =>
      config.allowed_masks?.some((mask) => mask.order_page_id === page.id),
    );

    if (orderPageWithConfig && orderPageWithConfig.working_days) {
      const workingMinutes = getDayWorkingMinutes(orderPageWithConfig.working_days, dayOfWeek);

      if (workingMinutes > 0) {
        // Multiply working minutes by the number of resources/stations
        const resourceCapacity = workingMinutes * (config.resource_count || 1);
        totalCapacityMinutes += resourceCapacity;
      }
    }
  }

  // Calculate capacity from regular workshops (non-shared resource workshops)
  // Only include workshops that are not part of shared resource configs
  const sharedResourceOrderPageIds = new Set(
    schedulerConfigs.flatMap((config) => config.allowed_masks?.map((mask) => mask.order_page_id) || []),
  );

  // Calculate capacity from regular workshops (non-shared resource workshops)
  // Only include workshops that are not part of shared resource configs
  for (const orderPage of orderPages) {
    // Only process if this order page is not already counted in shared resources
    if (!sharedResourceOrderPageIds.has(orderPage.id) && orderPage.working_days) {
      const workingMinutes = getDayWorkingMinutes(orderPage.working_days, dayOfWeek);
      // For regular workshops, we assume 1 capacity unit (no multiplication)
      totalCapacityMinutes += workingMinutes;
    }
  }

  return totalCapacityMinutes;
};

// Check if an order/event is within working hours for any workshop
const isWithinWorkingHours = (
  startTime: number,
  endTime: number,
  date: string,
  orderPages: OrderPageData[],
): boolean => {
  const momentDate = moment(date);
  const dayOfWeek = momentDate.day();

  for (const orderPage of orderPages) {
    if (orderPage.working_days) {
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayName = dayNames[dayOfWeek] as keyof WorkingDaysData;
      const dayData = orderPage.working_days[dayName];
      // working day with fixed start times foe not are not considered
      if (dayData && dayData.work_day_type === WorkDayType.INTERVAL && dayData.from_time && dayData.to_time) {
        // Check if the order/event overlaps with working hours
        const workingStart = dayData.from_time;
        const workingEnd = dayData.to_time;

        if (startTime < workingEnd && endTime > workingStart) {
          return true;
        }
      }
    }
  }

  return false;
};

// Calculate used minutes for orders and events on a specific date
const getUsedMinutesForDate = (
  date: string,
  orders: OrderData[],
  events: EnrichedEventData[],
  orderPages: OrderPageData[],
): { usedMinutes: number; orderCount: number; eventCount: number; personCount: number } => {
  let usedMinutes = 0;
  let orderCount = 0;
  let eventCount = 0;
  let personCount = 0;
  // Process orders
  for (const order of orders) {
    if (order.time !== undefined && order.duration !== undefined && !order.parent_id) {
      const startTime = order.time; // seconds from midnight
      const durationSeconds = order.duration; // duration is already in seconds
      const endTime = startTime + durationSeconds;

      if (isWithinWorkingHours(startTime, endTime, date, orderPages)) {
        const durationInMinutes = order.duration / 60; // Convert duration from seconds to minutes
        const resourceCount = seatedTableCount(order) || 1; // Number of resources used, default to 1
        const totalUsedMinutes = durationInMinutes * resourceCount; // Multiply by resource count

        usedMinutes += totalUsedMinutes;
        orderCount++;
        personCount += order.persons || 1;
      }
    }
  }

  // Process events
  for (const event of events) {
    if (event.time !== undefined && event.order_page_id) {
      // Find the corresponding order page to get duration
      const orderPage = orderPages.find((page) => page.id === event.order_page_id);
      if (orderPage && orderPage.duration) {
        const totalUsedMinutes = orderPage.duration; // orderPage.duration is already in minutes
        usedMinutes += totalUsedMinutes;
        eventCount++;
        personCount += event.used_tickets || 0;
      }
    }
  }

  return { usedMinutes, orderCount, eventCount, personCount };
};

// Calculate daily capacity stats
const calculateDailyStats = (
  calendarItems: CalendarItems,
  orderPages: OrderPageData[],
  account: AccountData,
  currentDate: string,
  numOfDays: number,
): DayCapacityStats[] => {
  const dailyStats: DayCapacityStats[] = [];

  for (let i = 0; i < numOfDays; i++) {
    const date = moment(currentDate).add(i, 'days').format('YYYY-MM-DD');
    const orders = calendarItems.orders[date] || [];
    const events = calendarItems.events[date] || [];

    const totalWorkingMinutes = getTotalCapacityMinutesForDate(date, orderPages, account);
    const { usedMinutes, orderCount, eventCount, personCount } = getUsedMinutesForDate(
      date,
      orders,
      events,
      orderPages,
    );

    const usagePercentage = totalWorkingMinutes > 0 ? (usedMinutes / totalWorkingMinutes) * 100 : 0;

    dailyStats.push({
      date,
      totalWorkingMinutes,
      usedMinutes,
      usagePercentage,
      orderCount,
      eventCount,
      personCount,
    });
  }

  return dailyStats;
};

// Aggregate daily stats into weekly/monthly periods
const aggregateStats = (dailyStats: DayCapacityStats[], periodType: 'week' | 'month'): PeriodCapacityStats[] => {
  const periods = new Map<string, DayCapacityStats[]>();

  for (const dayStats of dailyStats) {
    const date = moment(dayStats.date);
    const periodKey =
      periodType === 'week'
        ? date.clone().startOf('week').format('YYYY-MM-DD') // Week starts on Sunday
        : date.clone().startOf('month').format('YYYY-MM');

    if (!periods.has(periodKey)) {
      periods.set(periodKey, []);
    }
    periods.get(periodKey)!.push(dayStats);
  }

  return Array.from(periods.entries()).map(([periodKey, days]) => {
    const totalWorkingMinutes = days.reduce((sum, day) => sum + day.totalWorkingMinutes, 0);
    const usedMinutes = days.reduce((sum, day) => sum + day.usedMinutes, 0);
    const orderCount = days.reduce((sum, day) => sum + day.orderCount, 0);
    const eventCount = days.reduce((sum, day) => sum + day.eventCount, 0);
    const personCount = days.reduce((sum, day) => sum + day.personCount, 0);

    const usagePercentage = totalWorkingMinutes > 0 ? (usedMinutes / totalWorkingMinutes) * 100 : 0;
    const avgDailyUsage = days.length > 0 ? days.reduce((sum, day) => sum + day.usagePercentage, 0) / days.length : 0;

    const period =
      periodType === 'week'
        ? periodKey // Use week start date as period key (YYYY-MM-DD)
        : periodKey; // Use YYYY-MM format for monthly

    return {
      period,
      totalWorkingMinutes,
      usedMinutes,
      usagePercentage,
      orderCount,
      eventCount,
      avgDailyUsage,
      personCount,
    };
  });
};

// Main function to calculate capacity statistics
export const calculateCapacityStats = (
  calendarItems: CalendarItems,
  account: AccountData,
  currentDate: string,
  numOfDays: number,
): CapacityStats => {
  const orderPages = getOrderPages(account, true); // withArchived = true

  // Calculate daily stats
  const daily = calculateDailyStats(calendarItems, orderPages, account, currentDate, numOfDays);

  // Filter out days with no data for aggregation
  const daysWithData = daily.filter(
    (day) => day.totalWorkingMinutes > 0 || day.orderCount > 0 || day.eventCount > 0 || day.personCount > 0,
  );

  // Calculate weekly and monthly aggregations
  const weekly = daysWithData.length > 0 ? aggregateStats(daysWithData, 'week') : [];
  const monthly = daysWithData.length > 0 ? aggregateStats(daysWithData, 'month') : [];

  const result = {
    // Only show days with data
    daily: daily.filter(
      (day) => day.totalWorkingMinutes > 0 || day.orderCount > 0 || day.eventCount > 0 || day.personCount > 0,
    ),
    weekly,
    monthly,
  };

  return result;
};
